/*
 * Settings → Integrations. The COMPANY half of Microsoft Teams.
 *
 * ⚠️ **THIS IS NOT THE PROFILE CARD WITH DIFFERENT BUTTONS, AND THE SPLIT IS THE POINT.**
 * `NotificationChannelCard` answers *"where do MY notifications go"* and belongs to the
 * person. This answers *"is this company connected to Teams, and how do I connect it"* and
 * belongs to an administrator. Timetastic put theirs in Settings → Integrations for the
 * same reason — Rob's screenshot, 24/08/2026.
 *
 * ⚠️ **THERE IS NO ONE-CLICK "ADD TO TEAMS" BUTTON AND IT IS NOT AN OVERSIGHT.** Timetastic
 * have one because they are published in the Teams store and AppSource: for a listed app,
 * `https://teams.microsoft.com/l/app/<app-id>` opens the install dialog. For a sideloaded
 * package the id in our manifest becomes what Microsoft calls an *external* id, which deep
 * links do not accept — you would need a Graph lookup per tenant to find the id Teams
 * generated internally. **The button becomes real when the store submission lands
 * (`LAUNCH-CHECKLIST.md`, track D) and not before.** Building a link that silently does
 * nothing would be worse than the honest steps below.
 *
 * ⚠️ **TIDIED 27/10/2026 (`easy-installs`, Rob's 2a): SAME ROUTE, FEWER WORDS.** One row like
 * Timetastic's, a "Set up Microsoft Teams" button that opens the steps, and the old "Rolling it out
 * to everyone" card folded into step 2 — the admin-centre upload is the one that saves everybody
 * else a job. The steps are the ones Rob proved on 19/09; only the order and the words changed.
 * "Install it for everyone" (Manage apps → Edit installs) is NOT here yet: unproven on our app.
 *
 * ⚠️ **THE DOWNLOAD IS THE WHOLE VALUE HERE.** The package is built server-side from the
 * app id already saved in Superadmin → API Keys, so nobody types a GUID. On 23/08/2026 an
 * example id from the *sign-in* registration was pasted into `build-package.py`, and the
 * result installed cleanly and then failed with "Invalid Bot" — an hour lost to an error
 * that names nothing.
 */
import React, { useState, useEffect } from 'react';
import { Loader2, Download, MessageSquare, AlertTriangle, CheckCircle2, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { teamsApi, apiErrorMessage } from '../lib/api';
import { toast } from 'sonner';
import SlackIntegrationCard from './SlackIntegrationCard';
import IntegrationHeader from './IntegrationHeader';
import ApiKeysCard from './ApiKeysCard';
import WebhooksCard from './WebhooksCard';

/*
 * The Teams admin centre page where an uploaded app lives (Microsoft's own docs,
 * "Install Teams apps for users", checked 26/10/2026). ⚠️ It is the admin centre, NOT a
 * teams.microsoft.com/l/app deep link — see the top of this file for why that can't exist yet.
 */
export const TEAMS_ADMIN_MANAGE_APPS = 'https://admin.teams.microsoft.com/policies/manage-apps';

/*
 * ⚠️ The steps are DATA, not hard-coded blocks, so the wording changes in one place when the
 * store listing lands and step 1–2 become "click Add to Teams".
 *
 * ⚠️ **STEPS 3 AND 4 ARE TWO DIFFERENT INSTALLS, AND CONFLATING THEM COST A ROUND TRIP ON
 * 24/08/2026.** Uploading the package puts the app in a list; it does NOT put it in a team.
 * A bot is installed to a **team**, not to a channel — Microsoft: *"Teams bots can be
 * installed into personal chat, group chat, or a team (by default the General channel)."*
 * Somebody who looks in a channel's "+ Add an app" is in the tab picker, which will never
 * list HolidayHive.
 */
const STEPS = [
  {
    title: 'Download the app',
    text: 'It’s made for your company already — there’s nothing to fill in.',
    action: 'download',
  },
  {
    title: 'Add it to your company’s Teams',
    text: 'In the Teams admin centre: Teams apps → Manage apps → Upload new app, and choose the file. It then shows in your organisation’s app list, so nobody else has to upload anything. You’ll need to be a Teams administrator.',
    action: 'admin',
  },
  {
    title: 'Each person adds it',
    text: 'In Teams: Apps → find HolidayHive → Add. Then they choose Teams on their HolidayHive profile — nobody’s notifications change until they do.',
  },
  {
    title: 'Choose a team for the daily who’s-off post',
    text: 'Click the … beside the team’s name → Manage team → Apps → Upload a custom app, and choose the same file. It posts in that team’s General channel.',
  },
];

/*
 * ⚠️ Slack first: one button for the whole company, where Teams below needs a download and
 * four steps. Each card loads on its own, so a slow Teams check never hides the Slack card.
 */
export default function IntegrationsTab() {
  return (
    <div className="space-y-6">
      <SlackIntegrationCard />
      <TeamsIntegration />
      {/* DESIGN-api-and-webhooks.md step 1 — a company's own keys to read its data. */}
      <ApiKeysCard />
      {/* Step 2 (Rob's 8A): the second card, under API keys. */}
      <WebhooksCard />
    </div>
  );
}

function TeamsIntegration() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let live = true;
    teamsApi
      .status()
      .then((res) => { if (live) setStatus(res.data); })
      .catch(() => { if (live) setStatus(null); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await teamsApi.downloadPackage();
      /*
       * ⚠️ **THE OBJECT URL IS REVOKED AFTERWARDS.** Without it every click leaks the whole
       * zip for the lifetime of the tab. Small here, but this is the pattern the calendar
       * feed card had to have fixed for the same reason.
       */
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'holidayhive-teams.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('App downloaded.');
    } catch (e) {
      /*
       * ⚠️ **AN ERROR BODY ARRIVES AS A BLOB HERE, NOT AS JSON**, because the request asked
       * for one — so the 409 explaining that the bot is not set up would otherwise render
       * as "[object Blob]". Read it back as text before giving up on it.
       */
      let detail = null;
      try {
        const text = await e?.response?.data?.text?.();
        detail = text ? JSON.parse(text)?.detail : null;
      } catch (_) {
        detail = null;
      }
      toast.error(detail || apiErrorMessage(e, 'Couldn’t build the app.'));
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="card-hive p-6" data-testid="integrations-teams-card">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    );
  }

  const configured = !!status?.configured;
  const installed = status?.installed ?? 0;
  const people = status?.people ?? 0;

  const stepAction = (kind) => {
    if (kind === 'download') {
      return (
        <Button onClick={download} disabled={downloading} className="btn-primary" size="sm"
                data-testid="integrations-teams-download">
          {downloading
            ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            : <Download className="w-4 h-4 mr-2" />}
          Download the app
        </Button>
      );
    }
    if (kind === 'admin') {
      return (
        <Button asChild variant="outline" size="sm">
          <a href={TEAMS_ADMIN_MANAGE_APPS} target="_blank" rel="noopener noreferrer"
             data-testid="integrations-teams-admin-link">
            Open the Teams admin centre
            <ExternalLink className="w-3.5 h-3.5 ml-2" aria-hidden="true" />
          </a>
        </Button>
      );
    }
    return null;
  };

  return (
    <div className="card-hive p-6" data-testid="integrations-teams-card">
      <IntegrationHeader
        brand="teams"
        fallback={MessageSquare}
        name="Microsoft Teams"
        line="Approve leave from a Teams chat, and get a daily who’s-off post in a channel."
        place="Teams"
        status={installed > 0 && (
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                data-testid="integrations-teams-in-use">
            In use
          </span>
        )}
        action={configured && (
          <Button size="sm" variant={open ? 'outline' : 'default'} className={open ? '' : 'btn-primary'}
                  onClick={() => setOpen((v) => !v)} aria-expanded={open}
                  data-testid="integrations-teams-setup">
            {open ? 'Hide the steps' : 'Set up Teams'}
          </Button>
        )}
      />

      {!configured ? (
        /*
         * ⚠️ Names the screen. "Not configured" sends an administrator hunting through
         * Azure for something that is actually missing in our own superadmin area.
         */
        <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200"
             data-testid="integrations-teams-unconfigured">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-xs text-amber-800">
            The Teams bot isn’t set up on this installation yet. Add its app id and secret
            in Superadmin &rarr; API Keys, then come back here.
          </p>
        </div>
      ) : (
        <>
          {/*
            * ⚠️ **ZERO IS A REAL AND ORDINARY ANSWER**, so it must read as "nobody has
            * added it yet", never as a failure. Same trap as the calendar card's
            * "0 bookings in your calendar" beside a perfectly healthy connection.
            */}
          <p className="text-xs text-muted-foreground flex items-center gap-1.5"
             data-testid="integrations-teams-count">
            {installed > 0 && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" aria-hidden="true" />}
            {installed === 0
              ? 'Nobody has added the app in Teams yet.'
              : `${installed} of ${people} ${people === 1 ? 'person has' : 'people have'} added the app in Teams.`}
          </p>

          {open && (
            <>
              <ol className="mt-4" data-testid="integrations-teams-steps">
                {STEPS.map((step, index) => (
                  <li key={step.title} className="flex gap-3 py-3 border-t border-border">
                    <span className="w-6 h-6 shrink-0 rounded-full bg-primary/20 text-primary
                                     flex items-center justify-center text-xs font-medium">
                      {index + 1}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{step.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{step.text}</p>
                      {step.action && <div className="mt-2">{stepAction(step.action)}</div>}
                    </div>
                  </li>
                ))}
              </ol>

              {/*
                * ⚠️ Says the quiet part out loud rather than letting an administrator discover
                * it halfway through. Custom apps are off by default in plenty of tenants, and
                * the symptom is simply that the menu item is not there.
                */}
              <div className="border-t border-border pt-3 space-y-1.5 text-xs text-muted-foreground">
                <p>
                  Not a Teams administrator? You can try it on your own first: in Teams,
                  Apps &rarr; Manage your apps &rarr; Upload an app &rarr; Upload a custom app.
                </p>
                <p>
                  If “Upload” isn’t there at all, custom apps are turned off for your company —
                  Teams admin centre &rarr; Teams apps &rarr; Setup policies.
                </p>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
